import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { 
  FiCheck,
  FiClock,
  FiPause,
  FiSquare,
  FiTrash2,
  FiMoreVertical
} from 'react-icons/fi';
import { 
  getAllDetailTreatments,
  deleteDetailTreatment
} from '../../components/PatientTreatmentDetails/services/detailTreatmentService';

const DetailsTreatments = ({ 
  onCountUpdate, 
  componentName, 
  onKpiDataUpdate,
  externalFilters = {}, // Receive filters from parent
  externalSearchQuery = '' // Receive search query from parent
}) => {
  const [treatments, setTreatments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelection, setRowSelection] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  
  // Ref to track the table instance
  const tableRef = useRef(null);
  
  // Usar useRef para evitar recrear funciones en cada render
  const onKpiDataUpdateRef = useRef(onKpiDataUpdate);
  const onCountUpdateRef = useRef(onCountUpdate);
  
  // Actualizar refs cuando cambien las props
  useEffect(() => {
    onKpiDataUpdateRef.current = onKpiDataUpdate;
    onCountUpdateRef.current = onCountUpdate;
  }, [onKpiDataUpdate, onCountUpdate]);

  // Update global filter when external search query changes
  useEffect(() => {
    setGlobalFilter(externalSearchQuery || '');
  }, [externalSearchQuery]);

  // Función helper para extraer texto de un objeto o valor
  const extractText = useCallback((value, fallback = 'N/A') => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      return value.name || value.patient_name || value.doctor_name || value.toString() || fallback;
    }
    return String(value) || fallback;
  }, []);

  // Función helper mejorada para extraer información del paciente
  const extractPatientInfo = useCallback((treatment) => {
    const patientData = treatment.patient_id || treatment.patient_info || treatment.patient;
    
    let patientInfo = {
      patient_id: null,
      patient_name: null,
      patient_email: null,
      patient_phone: null
    };

    if (patientData) {
      patientInfo.patient_id = patientData._id || patientData.id || null;
      patientInfo.patient_name = patientData.name || null;
      patientInfo.patient_email = patientData.email || null;
      patientInfo.patient_phone = patientData.phone || null;
    }

    return patientInfo;
  }, []);

  // Función helper mejorada para extraer información del doctor
  const extractDoctorInfo = useCallback((treatment) => {
    const doctorData = treatment.doctor_id || treatment.doctor_info || treatment.doctor;
    
    let doctorInfo = {
      doctor_id: null,
      doctor_name: null,
      doctor_email: null,
      specialty: null
    };

    if (doctorData) {
      doctorInfo.doctor_id = doctorData._id || doctorData.id || null;
      doctorInfo.doctor_name = doctorData.name || null;
      doctorInfo.doctor_email = doctorData.email || null;
      doctorInfo.specialty = treatment.doctor_info?.specialty || null;
    }

    return doctorInfo;
  }, []);

  // Apply external filters to treatments data
  const applyExternalFilters = useCallback((treatmentsData, filters) => {
    if (!Array.isArray(treatmentsData) || treatmentsData.length === 0) {
      return treatmentsData;
    }

    let filtered = [...treatmentsData];

    // Apply ID search filter
    if (filters.idSearch && filters.idSearch.trim()) {
      const query = filters.idSearch.toLowerCase().trim();
      filtered = filtered.filter(treatment => 
        treatment.treatment_id?.toLowerCase().includes(query)
      );
    }

    // Apply patient name filter
    if (filters.patientName && filters.patientName.trim()) {
      const query = filters.patientName.toLowerCase().trim();
      filtered = filtered.filter(treatment => 
        treatment.patient_info?.patient_name?.toLowerCase().includes(query)
      );
    }

    // Apply doctor name filter
    if (filters.doctorName && filters.doctorName.trim()) {
      const query = filters.doctorName.toLowerCase().trim();
      filtered = filtered.filter(treatment => 
        treatment.doctor_info?.doctor_name?.toLowerCase().includes(query)
      );
    }

    // Apply selected treatments filter
    if (filters.selectedTreatments && filters.selectedTreatments.length > 0) {
      filtered = filtered.filter(treatment => {
        const treatmentSpecialty = treatment.doctor_info?.specialty;
        return filters.selectedTreatments.some(selectedTreatment => 
          treatmentSpecialty?.toLowerCase().includes(selectedTreatment.toLowerCase())
        );
      });
    }

    // Apply selected statuses filter
    if (filters.selectedStatuses && filters.selectedStatuses.length > 0) {
      filtered = filtered.filter(treatment => {
        const status = String(treatment.treatment_info?.status || '').toLowerCase();
        return filters.selectedStatuses.some(selectedStatus => 
          status === selectedStatus.toLowerCase()
        );
      });
    }

    // Apply progress filter (this might need adjustment based on your data structure)
    if (filters.progress !== undefined && filters.progress !== 50) {
      // Assuming progress is stored somewhere in the treatment data
      // This is a placeholder - adjust based on your actual progress data structure
      filtered = filtered.filter(treatment => {
        // You'll need to implement this based on how progress is calculated/stored
        // For now, this is a placeholder that always returns true
        return true;
      });
    }

    return filtered;
  }, []);

  // Función para calcular KPIs - ESTABLE
  const calculateKPIs = useCallback((data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return {
        totalTreatments: 0,
        uniqueDoctors: 0,
        uniquePatients: 0,
        completed: 0,
        active: 0,
        cancelled: 0,
        pending: 0,
        suspended: 0,
        consentSigned: 0,
        consentPending: 0
      };
    }

    const uniqueDoctorIds = new Set();
    const uniquePatientIds = new Set();
    
    let completed = 0;
    let active = 0;
    let cancelled = 0;
    let pending = 0;
    let suspended = 0;
    let consentSigned = 0;
    let consentPending = 0;

    data.forEach(treatment => {
      if (treatment.doctor_info?.doctor_id) {
        uniqueDoctorIds.add(treatment.doctor_info.doctor_id);
      }

      if (treatment.patient_info?.patient_id) {
        uniquePatientIds.add(treatment.patient_info.patient_id);
      }

      const status = String(treatment.treatment_info?.status || '').toLowerCase();
      switch (status) {
        case 'completed':
          completed++;
          break;
        case 'active':
        case 'in_progress':
          active++;
          break;
        case 'cancelled':
          cancelled++;
          break;
        case 'pending':
          pending++;
          break;
        case 'suspended':
          suspended++;
          break;
        default:
          active++;
          break;
      }

      if (treatment.consent_status?.is_required) {
        if (treatment.consent_status.is_signed) {
          consentSigned++;
        } else {
          consentPending++;
        }
      }
    });

    return {
      totalTreatments: data.length,
      uniqueDoctors: uniqueDoctorIds.size,
      uniquePatients: uniquePatientIds.size,
      completed,
      active,
      cancelled,
      pending,
      suspended,
      consentSigned,
      consentPending
    };
  }, []);

  // Apply external filters to get filtered data
  const filteredByExternalFilters = useMemo(() => {
    return applyExternalFilters(treatments, externalFilters);
  }, [treatments, externalFilters, applyExternalFilters]);

  // Función para obtener los datos actualmente visibles en la tabla
  const getVisibleTableData = useCallback((table) => {
    if (!table) return filteredByExternalFilters;
    
    try {
      const filteredRowModel = table.getFilteredRowModel();
      return filteredRowModel.rows.map(row => row.original);
    } catch (error) {
      console.error('Error getting visible table data:', error);
      return filteredByExternalFilters;
    }
  }, [filteredByExternalFilters]);

  // Cargar todos los tratamientos
  const loadAllTreatments = useCallback(async (page = 1, limit = 50) => {
    setIsLoading(true);
    try {
      const params = {
        page,
        limit,
        sort_by: 'treatment_info.last_update',
        sort_order: 'desc'
      };

      const response = await getAllDetailTreatments(params);
      console.log('Raw API response (all treatments):', response);
      
      let treatmentsData = [];
      
      if (response.data) {
        treatmentsData = Array.isArray(response.data) ? response.data : [response.data];
      } else if (Array.isArray(response)) {
        treatmentsData = response;
      } else if (response.treatments) {
        treatmentsData = Array.isArray(response.treatments) ? response.treatments : [response.treatments];
      } else if (response.results) {
        treatmentsData = Array.isArray(response.results) ? response.results : [response.results];
      }

      const normalizedTreatments = treatmentsData.map(treatment => {
        const patientInfo = extractPatientInfo(treatment);
        const doctorInfo = extractDoctorInfo(treatment);

        return {
          treatment_id: treatment.treatment_id || treatment._id || treatment.id || `temp-${Date.now()}-${Math.random()}`,
          patient_info: patientInfo,
          doctor_info: doctorInfo,
          treatment_info: {
            name: extractText(
              treatment.treatment_info?.name || 
              treatment.name || 
              treatment.treatment_name,
              'Unnamed Treatment'
            ),
            protocol_code: extractText(
              treatment.treatment_info?.protocol_code || 
              treatment.protocol_code || 
              treatment.code,
              '-'
            ),
            status: extractText(
              treatment.treatment_info?.status || 
              treatment.status,
              'active'
            ),
            start_date: treatment.treatment_info?.start_date || 
                       treatment.start_date || 
                       treatment.created_date ||
                       treatment.createdAt,
            estimated_end_date: treatment.treatment_info?.estimated_end_date || 
                               treatment.estimated_end_date || 
                               treatment.end_date,
            last_updated: treatment.treatment_info?.last_updated ||
                         treatment.last_updated ||
                         treatment.updatedAt ||
                         treatment.modified_date,
          },
          consent_status: {
            is_required: treatment.consent_status?.is_required ?? 
                        treatment.consent_required ?? 
                        true,
            is_signed: treatment.consent_status?.is_signed ?? 
                      treatment.consent_signed ?? 
                      (treatment.consent_status === 'signed') ?? 
                      false,
          },
          _original: treatment
        };
      });

      console.log('Normalized treatments:', normalizedTreatments);
      setTreatments(normalizedTreatments);

    } catch (error) {
      console.error('Error loading all treatments:', error);
      setTreatments([]);
    } finally {
      setIsLoading(false);
    }
  }, [extractPatientInfo, extractDoctorInfo, extractText]);

  // Cargar tratamientos al montar el componente
  useEffect(() => {
    loadAllTreatments();
  }, [loadAllTreatments]);

  // Memoizar los datos filtrados de la tabla incluyendo filtros externos y de tabla
  const finalFilteredData = useMemo(() => {
    if (!tableRef.current) return filteredByExternalFilters;
    return getVisibleTableData(tableRef.current);
  }, [filteredByExternalFilters, globalFilter, columnFilters, getVisibleTableData]);

  // Actualizar contador - SOLO cuando cambie la longitud
  useEffect(() => {
    if (onCountUpdateRef.current && typeof onCountUpdateRef.current === 'function' && componentName) {
      onCountUpdateRef.current(componentName, treatments.length);
    }
  }, [treatments.length, componentName]);

  // Actualizar KPIs basado en datos filtrados finales
  useEffect(() => {
    if (onKpiDataUpdateRef.current && typeof onKpiDataUpdateRef.current === 'function') {
      const kpiData = calculateKPIs(finalFilteredData);
      onKpiDataUpdateRef.current({
        allTreatments: treatments,
        visibleTreatments: finalFilteredData,
        filteredByExternalFilters: filteredByExternalFilters,
        kpiData: kpiData
      });
    }
  }, [finalFilteredData.length, treatments.length, filteredByExternalFilters.length, calculateKPIs]);

  // Debounced effect para actualizaciones de la tabla con menor frecuencia
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (tableRef.current && onKpiDataUpdateRef.current) {
        try {
          const visibleData = getVisibleTableData(tableRef.current);
          const kpiData = calculateKPIs(visibleData);
          
          onKpiDataUpdateRef.current({
            allTreatments: treatments,
            visibleTreatments: visibleData,
            filteredByExternalFilters: filteredByExternalFilters,
            kpiData: kpiData
          });
        } catch (error) {
          console.error('Error updating KPIs:', error);
        }
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [globalFilter, columnFilters, filteredByExternalFilters]);

  // Manejar filtro global
  const handleGlobalFilterChange = useCallback((filter) => {
    setGlobalFilter(filter);
  }, []);

  // Manejar cambios en filtros de columna
  const handleColumnFiltersChange = useCallback((filters) => {
    setColumnFilters(filters);
  }, []);

  // Manejar eliminación de tratamiento
  const handleDeleteTreatment = useCallback(async (treatmentId) => {
    if (!treatmentId) {
      console.error('Treatment ID is required for deletion');
      return;
    }

    const confirmDelete = window.confirm(
      'Are you sure you want to delete this treatment? This action cannot be undone.'
    );

    if (!confirmDelete) return;

    try {
      setIsLoading(true);
      console.log('Deleting treatment with ID:', treatmentId);
      
      await deleteDetailTreatment(treatmentId);
      
      setTreatments(prevTreatments => 
        prevTreatments.filter(treatment => treatment.treatment_id !== treatmentId)
      );
      
      console.log('Treatment deleted successfully');
      alert('Treatment deleted successfully');
      
    } catch (error) {
      console.error('Error deleting treatment:', error);
      alert(`Error deleting treatment: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Manejar selección múltiple
  const handleBulkDelete = useCallback(async () => {
    const selectedIds = Object.keys(rowSelection);
    
    if (selectedIds.length === 0) {
      alert('Please select treatments to delete');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedIds.length} treatment(s)? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      setIsLoading(true);
      
      const deletePromises = selectedIds.map(treatmentId => 
        deleteDetailTreatment(treatmentId)
      );
      
      await Promise.all(deletePromises);
      
      setTreatments(prevTreatments => 
        prevTreatments.filter(treatment => !selectedIds.includes(treatment.treatment_id))
      );
      
      setRowSelection({});
      
      console.log(`${selectedIds.length} treatments deleted successfully`);
      alert(`${selectedIds.length} treatment(s) deleted successfully`);
      
    } catch (error) {
      console.error('Error deleting treatments:', error);
      alert(`Error deleting treatments: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [rowSelection]);

  // Funciones de utilidad - ESTABLES
  const getStatusColor = useCallback((status) => {
    const statusLower = String(status || '').toLowerCase();
    switch (statusLower) {
      case 'completed':
        return 'success';
      case 'in_progress':
      case 'active':
        return 'warning';
      case 'pending':
        return 'secondary';
      case 'suspended':
        return 'danger';
      default:
        return 'primary';
    }
  }, []);

  const getStatusIcon = useCallback((status) => {
    const statusLower = String(status || '').toLowerCase();
    switch (statusLower) {
      case 'completed':
        return <FiCheck />;
      case 'in_progress':
      case 'active':
        return <FiClock />;
      case 'pending':
        return <FiPause />;
      case 'suspended':
        return <FiSquare />;
      default:
        return '•';
    }
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  }, []);

  // Columnas de la tabla - ESTABLES
  const columns = useMemo(() => [
    {
      accessorKey: 'treatment_id',
      header: 'Treatment ID',
      size: 150,
      Cell: ({ cell }) => (
        <div>
          <strong className="text-primary">
            {extractText(cell.getValue(), 'N/A')}
          </strong>
        </div>
      ),
    },
    {
      accessorKey: 'patient_info.patient_name',
      header: 'Patient',
      size: 200,
      Cell: ({ row }) => {
        const patientInfo = row.original.patient_info;
        return (
          <div>
            <strong className="text-primary">
              {patientInfo?.patient_name || 'Unknown Patient'}
            </strong>
            <div className="text-muted small">
               {patientInfo?.patient_id || 'N/A'}
            </div>
            {patientInfo?.patient_email && (
              <div className="text-muted small">
                {patientInfo.patient_email}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'doctor_info.doctor_name',
      header: 'Doctor',
      size: 200,
      Cell: ({ row }) => {
        const doctorInfo = row.original.doctor_info;
        return (
          <div>
            <strong className="text-info">
              {doctorInfo?.doctor_name || 'Unknown Doctor'}
            </strong>
            <div className="text-muted small">
              {doctorInfo?.doctor_id || 'N/A'}
            </div>
            {doctorInfo?.doctor_email && (
              <div className="text-muted small">
                {doctorInfo.doctor_email}
              </div>
            )}
            {doctorInfo?.specialty && (
              <div className="text-muted small">
                Specialty: {doctorInfo.specialty}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'treatment_info.name',
      header: 'Treatment Name',
      size: 200,
      Cell: ({ cell, row }) => (
        <div>
          <strong>{extractText(cell.getValue(), 'Unnamed Treatment')}</strong>
          {row.original.treatment_info?.protocol_code && (
            <div className="text-muted small">
              Code: {extractText(row.original.treatment_info.protocol_code, '-')}
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'treatment_info.start_date',
      header: 'Start Date',
      size: 120,
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    {
      accessorKey: 'treatment_info.estimated_end_date',
      header: 'End Date',
      size: 120,
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    {
      accessorKey: 'treatment_info.last_updated',
      header: 'Last Update',
      size: 120,
      Cell: ({ cell, row }) => {
        const lastUpdated = cell.getValue() || 
                           row.original.updated_date || 
                           row.original.treatment_info?.start_date ||
                           row.original.created_date;
        return formatDate(lastUpdated);
      },
    },
    {
      accessorKey: 'treatment_info.status',
      header: 'Status',
      size: 120,
      Cell: ({ cell }) => {
        const status = extractText(cell.getValue(), 'unknown');
        const color = getStatusColor(status);
        const icon = getStatusIcon(status);
        
        return (
          <span className={`badge bg-${color} d-flex align-items-center gap-1`}>
            <span>{icon}</span>
            {status.replace('_', ' ').toUpperCase()}
          </span>
        );
      },
    },
    {
      accessorKey: 'consent_status.is_signed',
      header: 'Consent',
      size: 80,
      Cell: ({ cell, row }) => {
        const isSignedValue = cell.getValue();
        const consentStatus = row.original.consent_status;
        
        if (!consentStatus?.is_required) {
          return <span className="text-muted">N/A</span>;
        }
        
        if (isSignedValue) {
          return <span className="text-success"><FiCheck /> Signed</span>;
        }
        
        return <span className="text-warning">Pending</span>;
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 80,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={() => handleDeleteTreatment(row.original.treatment_id)}
            disabled={isLoading || !row.original.treatment_id}
            title="Delete Treatment"
          >
            <FiTrash2 size={14} />
          </button>
        </div>
      ),
    },
  ], [formatDate, getStatusColor, getStatusIcon, handleDeleteTreatment, isLoading, extractText]);

  return (
    <>      

      
        <MaterialReactTable
          ref={tableRef}
          columns={columns}
          data={filteredByExternalFilters} // Use externally filtered data as base
          enableRowSelection={true}
          getRowId={(row) => row.treatment_id}
          onRowSelectionChange={setRowSelection}
          state={{
            globalFilter,
            isLoading,
            rowSelection,
            columnFilters
          }}
          onGlobalFilterChange={handleGlobalFilterChange}
          onColumnFiltersChange={handleColumnFiltersChange}
          enableGlobalFilter
          enableSorting
          enableColumnFilters={true}
          enableFilters={true}
          enablePagination
          muiTableHeadCellProps={{
            sx: {
              fontWeight: 600
            }
          }}
          muiSearchTextFieldProps={{
            placeholder: 'Search treatments, patients, doctors...',
            sx: { minWidth: '300px' },
            variant: 'outlined'
          }}
          muiTablePaginationProps={{
            showFirstButton: true,
            showLastButton: true
          }}
          muiSelectCheckboxProps={{
            color: 'primary',
          }}
          muiSelectAllCheckboxProps={{
            color: 'primary',
          }}
          initialState={{
            pagination: {
              pageSize: 10,
              pageIndex: 0
            },
            sorting: [
              {
                id: 'treatment_info.last_updated',
                desc: true
              }
            ]
          }}
        />
      
    </>
  );
};

export default DetailsTreatments;